/**
 * WHAT A PRACTICE WRITE IS ANSWERED WITH, where the generic answer would teach the wrong thing.
 *
 * practiceMode's default answer echoes the request body back with a synthesised id, and for most of
 * this app's create endpoints that IS what the server returns, so the next screen works and nothing
 * needs to be written here.
 *
 * It is not enough wherever the SERVER computes something the client could not have known. A sticker
 * is the clearest case: `code` and `url_path` are minted server-side (forms backend
 * sticker_service.public_url_path), and EquipmentStickers hands the created row straight to the print
 * sheet (`setPrinting([created])`). Answered generically, a practice run would walk somebody through
 * printing a BLANK LABEL and call it a success — a tour that "works" while teaching the wrong thing
 * is worse than one that visibly breaks.
 *
 * Importing this module registers the entries. TourProvider imports it for its side effect, which is
 * the one place a tour can start, so there is no ordering to get wrong.
 *
 * ── EVERY VALUE HERE IS DELIBERATELY MARKED ─────────────────────────────────────────────────────
 * A practice code reads PRACTICE… so a label that somehow reaches a printer is obviously not a real
 * one, and anybody who scans it gets nothing. Do not make these look authentic.
 */
import { registerPracticeResponse } from './practiceMode'

const now = () => new Date().toISOString()

/**
 * A real code is 24 Crockford-base32 characters. This is 24 characters that spell a warning, and it
 * contains O and L — which Crockford base32 EXCLUDES — so it can never collide with a real code no
 * matter how many are ever minted. StickerPrintSheet prints codes in groups of four, so on a label
 * it reads "PRAC TICE ONLY NOTA REAL CODE".
 */
const PRACTICE_STICKER_CODE = 'PRACTICEONLYNOTAREALCODE'

registerPracticeResponse(
  (method, path) => method === 'POST' && /\/forms-settings\/stickers$/.test(path),
  ({ body }) => ({
    id: 'practice-sticker',
    code: PRACTICE_STICKER_CODE,
    unit_id: (body && body.unit_id) || null,
    unit_label: (body && body.unit_label) || null,
    template_id: (body && body.template_id) || null,
    status: 'active',
    // A path that resolves to nothing anywhere. The public route is /inspect/{tenant}/{code} and no
    // tenant is called this, so a scan of a practice label fails cleanly rather than opening a form.
    url_path: `/inspect/practice/${PRACTICE_STICKER_CODE}`,
    practice: true,
    created_at: now(),
  }),
)

/**
 * SUGGESTING a field PIN — a POST that reads, and the one shape this mechanism gets wrong by design.
 *
 * `POST /employees/field-pins/suggest` mutates nothing (hr backend api/field_pins.py: "touches no
 * row, and returns a value that is not yet anybody's credential"). Practice mode blocks by METHOD,
 * which is the correct rule — it is simple and auditable, and a rule that asked each endpoint whether
 * it "really" writes would be neither. But that rule made "Generate one for me" return the generic
 * answer, `response.data.pin` came back undefined, `setPinDraft(undefined)` left the box EMPTY, and
 * the next click failed the panel's own 6-to-8-digit check — whose red message renders behind the
 * tour card. The reader sees a button that does nothing, and step 11 of the field-PIN walkthrough
 * waits forever for a reveal panel that can never appear. Reported from staging as exactly that:
 * "setting up the machine stalls at step 11. pressing the set the pin button does nothing".
 *
 * So it is answered here rather than let through: nothing may leave the browser during a practice
 * run, including a POST that happens to be harmless today, because the next person to add a side
 * effect to it will not think to check this file.
 *
 * A DIFFERENT PIN each time, because "generate another" is a thing the tour tells the reader to do
 * and a constant would quietly teach the opposite. Six digits (PIN_MIN_LENGTH), and none of these is
 * a run, a repeat, or readable as a date — the three families the real generator refuses — so the
 * copy above the button stays true of what the button produces.
 */
const PRACTICE_PINS = ['284617', '739502', '481963', '506218', '927340']
let practicePinTurn = 0
registerPracticeResponse(
  (method, path) => method === 'POST' && /\/field-pins\/suggest$/.test(path),
  () => {
    const pin = PRACTICE_PINS[practicePinTurn % PRACTICE_PINS.length]
    practicePinTurn += 1
    return { pin, practice: true }
  },
)

/**
 * Issuing a field PIN.
 *
 * The response never contains the PIN — that is a rule of the real endpoint (hr backend
 * api/field_pins.py) and a practice answer that broke it would teach the screen to expect something
 * the server does not send. `has_pin` is the flag the panel actually branches on, and it is what
 * makes the show-it-once panel appear, which is the step the tour is about.
 */
registerPracticeResponse(
  (method, path) => method === 'PUT' && /\/field-pin$/.test(path),
  () => ({
    has_pin: true,
    set_at: now(),
    last_used_at: null,
    failed_attempts: 0,
    is_locked: false,
    locked_until: null,
    practice: true,
  }),
)

/**
 * ── THE DAILY REPORT: VERIFY AND SUBMIT RETURN A VERDICT, NOT A ROW ─────────────────────────────
 *
 * Both are POSTs whose answer is a JUDGEMENT the client cannot invent, and the generic echo-the-body
 * answer gets it exactly backwards. `handleSubmit` reads `res.data.submitted`; absent, it takes the
 * failure branch and puts "Cannot submit — fix errors below" on screen in red. So the final step of
 * the flagship tour — the one teaching a foreman what sending a day's work looks like — ended on a
 * refusal it invented itself, for a report that was fine.
 *
 * Answering "it went through" is honest here in a way it would not be on a real submit, because the
 * client has ALREADY decided: DailyReportEntry computes `submitBlockers` itself and refuses to call
 * either endpoint while that list is non-empty (DailyReportEntry.jsx:1205, :1225). A request that
 * reaches this file is therefore one the screen has already found nothing wrong with — and there is
 * no server-side second opinion to be had, because practice mode blocked the save too and the server
 * has never seen this report.
 */
registerPracticeResponse(
  (method, path) => method === 'POST' && /\/daily-reports\/[^/]+\/verify$/.test(path),
  () => ({ can_submit: true, errors: [], warnings: [], missing_forms: [], practice: true }),
)

registerPracticeResponse(
  (method, path) => method === 'POST' && /\/daily-reports\/[^/]+\/submit$/.test(path),
  () => ({ submitted: true, practice: true }),
)

/**
 * ── APPROVING CREW HOURS: THE ANSWER REPLACES THE ROW ───────────────────────────────────────────
 *
 * WeeklyTimeView takes the response and puts it in the list IN PLACE OF the block it approved. The
 * generic answer carries an id, a `practice` flag and two timestamps — no employee_id, no
 * employee_name, no report_date, no total_hours — so the approved block regrouped under an undefined
 * employee and the page died on `emp.employee_name.charAt(0)`. A foreman on a practice run pressed
 * Approve and got a white screen.
 *
 * This answers with the ONE FIELD the screen is actually waiting for and nothing else, which is why
 * the page's two handlers now merge the answer over the block they already hold instead of replacing
 * it (WeeklyTimeView.jsx). Between them, the day turns green, keeps its hours and its date, and
 * nobody's time is signed off.
 *
 * Deliberately NOT reconstructing a block: everything else about it — whose day it was, which job,
 * how many hours — is knowledge this file does not have, and inventing it is how a training exercise
 * ends up teaching somebody a number.
 */
registerPracticeResponse(
  (method, path) => method === 'POST' && /\/employee-hours\/(crew\/)?[^/]+\/acknowledge$/.test(path),
  () => ({ employee_acknowledged: true, practice: true }),
)

/**
 * ── FILLING A FORM: THE WRITE HAPPENS BEFORE THE SHEET IS EVEN ON SCREEN ────────────────────────
 *
 * This one is not "the last step shows the wrong banner". Opening a sheet CREATES the draft — FormFill
 * POSTs /form-submissions before a character is typed and renders off the answer — so under the
 * generic echo a practice run of the JSA tour rendered, in full:
 *
 *     draft   This form has already been submitted, so it’s read-only.   This form has no fields yet.
 *
 * …with ZERO of the seven field anchors and no Submit button on the page. Eleven of the JSA tour's
 * fourteen steps pointed at nothing. Measured, not reasoned about — see "a practice run on the JSA
 * sheet" in __tests__/tourWalkthrough.test.jsx, which is the assertion that keeps it fixed.
 *
 * Two fields did it, and both are SERVER knowledge that the echoed request body cannot carry:
 *
 *   status       absent → `setReadOnly(sub.status !== 'draft')` is true (FormFill.jsx:179), so the
 *                sheet renders in read-only mode: no inputs, and the whole sticky Submit bar — which
 *                is where `forms.fill.submit` lives — is not rendered at all.
 *   version_id   absent → the schema is fetched from /form-versions/undefined/schema, which answers
 *                nothing, so the sheet has no fields to anchor.
 *
 * `version_id` is the one thing this file genuinely cannot supply: the server pins it at creation and
 * nothing in the request says which version that is. FormFill therefore falls back to the template's
 * `current_version_id` — the value the server would have pinned — when a created draft carries none;
 * see the note there. This answer supplies everything else.
 */
registerPracticeResponse(
  (method, path) => method === 'POST' && /\/form-submissions$/.test(path),
  ({ body }) => ({
    id: 'practice-submission',
    template_id: (body && body.template_id) || null,
    // Deliberately null rather than invented — see above. FormFill resolves it from the template.
    version_id: null,
    status: 'draft',
    context: (body && body.context) || 'standalone',
    reference_type: (body && body.reference_type) || null,
    reference_id: (body && body.reference_id) || null,
    payload_json: (body && body.payload_json) || {},
    record_number: null,
    row_version: 0,
    submitted_at: null,
    practice: true,
    created_at: now(),
  }),
)

/**
 * The autosave. `doSave` reads `res.row_version` and hands it to the next PATCH as the optimistic
 * lock, and `handleSubmit` REFUSES to submit at all if this call did not resolve (FormFill.jsx:307).
 *
 * The generic echo happens to work here — the client puts `row_version` in the request body, so the
 * body that comes back has one. That is an accident of where the field is sent, and it stops being
 * true the day somebody moves the lock into a header or a query string, at which point the version
 * silently stops advancing and nothing fails. Answering it explicitly makes "the save landed" a
 * property of this file rather than a coincidence, and moves the version the way the server does.
 */
registerPracticeResponse(
  (method, path) => method === 'PATCH' && /\/form-submissions\/[^/]+$/.test(path),
  ({ body }) => ({
    payload_json: (body && body.payload_json) || {},
    row_version: (body && typeof body.row_version === 'number' ? body.row_version : 0) + 1,
    status: 'draft',
    practice: true,
    updated_at: now(),
  }),
)

/**
 * Sending the sheet. `handleSubmit` puts the answer's `status` and `record_number` on screen.
 *
 * NO RECORD NUMBER, ON PURPOSE, and it is the same rule as the sticker code above. The number is a
 * sequential ticket drawn from an advisory-lock counter that CANNOT hand one back
 * (2026_07_20_forms_submission_record_number.sql), so there is no honest value to put here — and a
 * plausible-looking "#14" is a number somebody quotes back at the office six months later. The
 * confirmation screen renders the number only when it has one (FormFill.jsx:370), so a practice run
 * gets "Submitted" with no receipt against it, which is exactly true. The tour says what the number
 * is on `send-it`, where the claim is still true, and no step lives on that screen.
 */
registerPracticeResponse(
  (method, path) => method === 'POST' && /\/form-submissions\/[^/]+\/submit$/.test(path),
  () => ({
    status: 'submitted',
    record_number: null,
    submitted_at: now(),
    practice: true,
  }),
)

export default null
